/**
 * Integrytas Group - Common Security Module (CSM)
 * Contextual AI Assistance — browser handle to the csm-ai Edge Function.
 *
 * Mirrors the api_client seam: the browser holds NO provider key. Every request
 * is a single authenticated JSON round-trip to the csm-ai gateway, which
 * enforces per-screen opt-in, PII redaction, role guardrails and audit logging
 * server-side. The caller's Supabase JWT is borrowed for authentication exactly
 * as api_client does, so RLS + forensic context are identical.
 *
 * Per-screen configuration is read through the existing csm-api gateway
 * (apiDb.rpc → csm_ai_resolve_config) so there is only one place that resolves
 * "is AI allowed here?".
 */

import { supabase } from '../supabase_client';
import { apiDb } from '../api_client';
import {
  AI_DISABLED_CONFIG,
  type AiColumnSchema,
  type AiDocumentInput,
  type AiExtractResult,
  type AiFieldSchema,
  type AiNlqResult,
  type AiScreenConfig,
} from './types';

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
const AI_GATEWAY_URL    = `${SUPABASE_URL}/functions/v1/csm-ai`;

interface AiEnvelope<T> { data: T | null; error: { message: string } | null }

async function aiFetch<T>(body: Record<string, unknown>): Promise<AiEnvelope<T>> {
  let token = SUPABASE_ANON_KEY;
  try {
    const { data } = await supabase.auth.getSession();
    if (data.session?.access_token) token = data.session.access_token;
  } catch { /* fall back to anon */ }

  try {
    const resp = await fetch(AI_GATEWAY_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`,
        'x-csm-client': 'csm-web',
      },
      body: JSON.stringify(body),
    });
    const jsonBody = await resp.json().catch(() => ({ data: null, error: `CSM.AI.BAD_RESPONSE (${resp.status})` }));
    const errMsg: string | null = jsonBody.error ?? (resp.ok ? null : `CSM.AI.HTTP_${resp.status}`);
    return { data: (jsonBody.data ?? null) as T | null, error: errMsg ? { message: errMsg } : null };
  } catch (e) {
    return { data: null, error: { message: (e as Error).message } };
  }
}

export interface NlqRequest {
  pageCode:      string;
  query:         string;
  columns:       AiColumnSchema[];
  sampleRows?:   Record<string, unknown>[];
  permissions?:  Record<string, unknown>;
}

export interface ExtractRequest {
  pageCode:  string;
  fields:    AiFieldSchema[];
  text?:     string;
  document?: AiDocumentInput;
}

export const aiClient = {
  /** Resolve whether AI is allowed on a screen (folds in the global switch). */
  async getConfig(pageCode: string): Promise<AiScreenConfig> {
    const { data, error } = await apiDb.rpc<AiScreenConfig>('csm_ai_resolve_config', {
      p_page_code: pageCode,
    });
    if (error || !data) return { ...AI_DISABLED_CONFIG, page_code: pageCode };
    return data;
  },

  /** Translate natural language into a native filter/sort AiQuery. */
  async nlq(req: NlqRequest): Promise<AiEnvelope<AiNlqResult>> {
    return aiFetch<AiNlqResult>({
      kind:        'nlq',
      page_code:   req.pageCode,
      query:       req.query,
      schema:      { columns: req.columns },
      sample_rows: req.sampleRows,
      permissions: req.permissions,
    });
  },

  /** Extract structured form values from unstructured text or a document. */
  async extract(req: ExtractRequest): Promise<AiEnvelope<AiExtractResult>> {
    return aiFetch<AiExtractResult>({
      kind:      'extract',
      page_code: req.pageCode,
      fields:    req.fields,
      text:      req.text,
      document:  req.document,
    });
  },

  /** Machine-translate a batch of UI strings into a target CSM locale. Best-effort
   *  (returns an error if no AI provider key is configured on the stack). */
  async translate(texts: string[], targetLocale: string): Promise<AiEnvelope<{ translations: string[] }>> {
    return aiFetch<{ translations: string[] }>({
      kind:          'translate',
      texts,
      target_locale: targetLocale,
    });
  },
};

export type AiClient = typeof aiClient;
